if (!customElements.get('tooto-technology-tabs')) {
  class TootoTechnologyTabs extends HTMLElement {
    constructor() {
      super();
      this.records = [];
      this.isInitialized = false;
      this.hasActivated = false;
      this.panelAnimation = null;
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) return;

      this.isInitialized = true;
      this.tablist = this.querySelector('[data-technology-tablist]');
      this.track = this.querySelector('[data-technology-tab-track]');
      this.itemsContainer = this.querySelector('[data-technology-tab-items]');

      if (!this.tablist || !this.track || !this.itemsContainer) return;

      const items = Array.from(this.itemsContainer.children).filter((item) =>
        item.matches('[data-technology-tab-item]')
      );
      const existingTabs = Array.from(
        this.tablist.querySelectorAll('[data-technology-tab-trigger]')
      );

      this.records = items
        .map((item) => {
          const blockId = item.dataset.blockId;
          const tab =
            item.querySelector('[data-technology-tab-trigger]') ||
            existingTabs.find((candidate) => candidate.dataset.blockId === blockId);
          const panel = item.querySelector('[data-technology-tab-panel]');

          if (!blockId || !tab || !panel) return null;
          if (tab.parentElement !== this.track) this.track.append(tab);

          return { blockId, item, tab, panel };
        })
        .filter(Boolean);

      if (!this.records.length) {
        this.classList.add('is-empty');
        return;
      }

      this.classList.remove('is-empty');
      this.tablist.addEventListener('click', this.handleClick);
      this.tablist.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);

      this.activate(this.records[0].blockId, { animate: false });
      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      this.tablist?.removeEventListener('click', this.handleClick);
      this.tablist?.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.panelAnimation?.cancel();
      this.panelAnimation = null;
      this.records = [];
      this.hasActivated = false;
      this.isInitialized = false;
    }

    handleClick(event) {
      const tab = event.target.closest('[data-technology-tab-trigger]');
      if (!tab || !this.tablist.contains(tab)) return;

      this.activate(tab.dataset.blockId, { focus: false, scroll: true });
    }

    handleKeydown(event) {
      const currentTab = event.target.closest('[data-technology-tab-trigger]');
      if (!currentTab) return;

      const currentIndex = this.records.findIndex((record) => record.tab === currentTab);
      if (currentIndex < 0) return;

      const isRtl = document.documentElement.dir === 'rtl';
      let nextIndex = currentIndex;

      switch (event.key) {
        case 'ArrowRight':
          nextIndex = isRtl ? currentIndex - 1 : currentIndex + 1;
          break;
        case 'ArrowLeft':
          nextIndex = isRtl ? currentIndex + 1 : currentIndex - 1;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = this.records.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      nextIndex = (nextIndex + this.records.length) % this.records.length;
      this.activate(this.records[nextIndex].blockId, { focus: true, scroll: true });
    }

    handleBlockSelect(event) {
      const selectedRecord = this.records.find((record) => {
        if (event.detail?.blockId === record.blockId) return true;
        return event.target instanceof Node && record.item.contains(event.target);
      });

      if (!selectedRecord) return;
      this.activate(selectedRecord.blockId, { focus: false, scroll: true });
    }

    activate(blockId, options = {}) {
      const activeRecord = this.records.find((record) => record.blockId === blockId);
      if (!activeRecord) return;

      const previousIndex = this.records.findIndex(
        (record) => record.tab.getAttribute('aria-selected') === 'true'
      );
      const activeIndex = this.records.indexOf(activeRecord);
      const shouldAnimate =
        this.hasActivated &&
        options.animate !== false &&
        previousIndex !== activeIndex &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.records.forEach((record) => {
        const isActive = record === activeRecord;
        record.item.classList.toggle('is-active', isActive);
        record.tab.setAttribute('aria-selected', String(isActive));
        record.tab.tabIndex = isActive ? 0 : -1;
        record.panel.hidden = !isActive;
      });

      if (shouldAnimate) {
        this.animatePanel(activeRecord.panel);
      }

      this.hasActivated = true;

      if (options.focus) activeRecord.tab.focus({ preventScroll: true });

      if (options.scroll) {
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        activeRecord.tab.scrollIntoView({
          behavior: reducedMotion ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }

    animatePanel(panel) {
      if (typeof panel.animate !== 'function') return;

      this.panelAnimation?.cancel();
      this.panelAnimation = panel.animate(
        [
          { opacity: 0, transform: 'translateY(24px)' },
          { opacity: 1, transform: 'translateY(0)' },
        ],
        {
          duration: 600,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        }
      );
      this.panelAnimation.addEventListener(
        'finish',
        () => {
          this.panelAnimation = null;
        },
        { once: true }
      );
    }
  }

  customElements.define('tooto-technology-tabs', TootoTechnologyTabs);
}
